import { useState, useEffect, useCallback } from 'react'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import Logo from '../components/Logo'
import {
  Car,
  Wrench,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Shield,
  Star,
  Bell,
  TrendingUp,
  ChevronRight,
  Home,
  Store,
  Headphones
} from 'lucide-react'

// Navigation items with categories — Concierge is elevated as primary feature
const navSections = [
  {
    label: 'Home',
    items: [
      { to: '/', icon: Home, label: 'Dashboard' },
      { to: '/concierge', icon: Headphones, label: 'Concierge', highlight: true },
    ]
  },
  {
    label: 'My Collection',
    items: [
      { to: '/garage', icon: Car, label: 'My Garage' },
      { to: '/service-history', icon: Wrench, label: 'Service History' },
      { to: '/documents', icon: FileText, label: 'Documents' },
      { to: '/valuations', icon: TrendingUp, label: 'Valuations' },
      { to: '/maintenance', icon: Bell, label: 'Reminders' },
    ]
  },
  {
    label: 'More',
    items: [
      { to: '/specialists', icon: Star, label: 'Trusted Specialists' },
      { to: '/my-listing', icon: Store, label: 'My Listing' },
    ]
  }
]

export default function ClientLayout() {
  const { profile, signOut, isAdmin, user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [hasUnreadConcierge, setHasUnreadConcierge] = useState(false)

  // Check for unread concierge messages
  const checkUnread = useCallback(async () => {
    if (!user?.id) return
    try {
      const lastSeen = localStorage.getItem(`rdc_concierge_seen_${user.id}`) || '1970-01-01T00:00:00Z'

      // Get user's sessions, then check for assistant messages after lastSeen
      const { data: sessions } = await supabase
        .from('ai_chat_sessions')
        .select('id')
        .eq('client_id', user.id)

      if (!sessions?.length) return

      const sessionIds = sessions.map(s => s.id)
      const { count } = await supabase
        .from('ai_chat_messages')
        .select('id', { count: 'exact', head: true })
        .in('session_id', sessionIds)
        .eq('role', 'assistant')
        .gt('created_at', lastSeen)

      setHasUnreadConcierge(count > 0)
    } catch (err) {
      console.error('Failed to check unread concierge messages:', err)
    }
  }, [user?.id])

  // Mark concierge as seen when user visits the page
  useEffect(() => {
    if (location.pathname === '/concierge' && user?.id) {
      localStorage.setItem(`rdc_concierge_seen_${user.id}`, new Date().toISOString())
      setHasUnreadConcierge(false)
    }
  }, [location.pathname, user?.id])

  // Poll for unread messages every 30 seconds
  useEffect(() => {
    checkUnread()
    const interval = setInterval(checkUnread, 30000)
    return () => clearInterval(interval)
  }, [checkUnread])

  const handleSignOut = async () => {
    await signOut()
    navigate('/login')
  }

  const NavItem = ({ to, icon: Icon, label, highlight }) => (
    <NavLink
      to={to}
      onClick={() => setMobileMenuOpen(false)}
      className={({ isActive }) =>
        highlight
          ? `flex items-center gap-2.5 px-6 py-2.5 text-sm font-semibold transition-colors ${
              isActive
                ? 'text-rdc-primary bg-rdc-primary/10 border-r-2 border-rdc-primary'
                : 'text-rdc-primary hover:bg-rdc-primary/5'
            }`
          : `sidebar-nav-item ${isActive ? 'active' : ''}`
      }
    >
      <Icon size={18} strokeWidth={1.75} />
      <span>{label}</span>
      {to === '/concierge' && hasUnreadConcierge && (
        <span className="ml-auto w-2.5 h-2.5 rounded-full bg-rdc-orange animate-pulse" />
      )}
    </NavLink>
  )

  return (
    <div className="flex min-h-screen bg-rdc-cream">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2.5 bg-white rounded-lg shadow-md border border-rdc-stone/30"
      >
        {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-40
          w-[260px] sidebar flex flex-col
          transform transition-transform duration-300 ease-out
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className="p-6 flex justify-center">
          <Logo linkTo="/" />
        </div>

        {/* Divider */}
        <div className="mx-6 divider-solid" />

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {navSections.map((section, idx) => (
            <div key={section.label} className={idx > 0 ? 'mt-5' : ''}>
              <div className="px-6 mb-2">
                <span className="text-label">{section.label}</span>
              </div>
              {section.items.map((item) => (
                <NavItem key={item.to} {...item} />
              ))}
            </div>
          ))}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-rdc-stone/30">
          {/* Admin Link */}
          {isAdmin && (
            <NavLink
              to="/admin"
              className="flex items-center gap-2.5 px-3 py-2.5 mb-3 text-sm text-rdc-forest bg-rdc-forest/8 rounded-lg hover:bg-rdc-forest/12 transition-colors"
            >
              <Shield size={16} strokeWidth={1.75} />
              <span className="font-medium">Admin Dashboard</span>
              <ChevronRight size={14} className="ml-auto opacity-50" />
            </NavLink>
          )}

          {/* Profile */}
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-rdc-stone to-rdc-taupe flex items-center justify-center">
              <span className="text-sm font-medium text-white">
                {profile?.full_name?.charAt(0) || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-black truncate">
                {profile?.full_name || 'User'}
              </div>
              <div className="text-xs text-rdc-taupe">
                Member since {new Date(profile?.member_since || profile?.created_at).getFullYear()}
              </div>
            </div>
          </div>

          {/* Settings & Logout */}
          <div className="flex items-center gap-1 mt-3 pt-3 border-t border-rdc-stone/20">
            <NavLink
              to="/settings"
              onClick={() => setMobileMenuOpen(false)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-rdc-taupe hover:text-rdc-charcoal hover:bg-rdc-cream/70 rounded-lg transition-colors"
            >
              <Settings size={14} strokeWidth={1.75} />
              <span>Settings</span>
            </NavLink>
            <div className="w-px h-4 bg-rdc-stone/30" />
            <button
              onClick={handleSignOut}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-rdc-taupe hover:text-rdc-burgundy hover:bg-rdc-burgundy/5 rounded-lg transition-colors"
            >
              <LogOut size={14} strokeWidth={1.75} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-4 pt-16 sm:p-6 sm:pt-6 lg:p-10 max-w-7xl mx-auto">
          <Outlet />
        </div>

        {/* Footer */}
        <footer className="mt-12 border-t border-rdc-stone/20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6">
            <div className="flex flex-wrap gap-6 text-sm">
              <a href="/" className="text-rdc-taupe hover:text-rdc-charcoal transition-colors">
                Home
              </a>
              <span className="text-rdc-stone">/</span>
              <a href="/privacy" className="text-rdc-taupe hover:text-rdc-charcoal transition-colors">
                Privacy Policy
              </a>
              <span className="text-rdc-stone">/</span>
              <a href="/terms" className="text-rdc-taupe hover:text-rdc-charcoal transition-colors">
                Terms of Service
              </a>
              <span className="text-rdc-stone">/</span>
              <a href="/sms-consent" className="text-rdc-taupe hover:text-rdc-charcoal transition-colors">
                SMS Consent
              </a>
            </div>
            <p className="text-xs text-rdc-taupe mt-4">
              © 2026 Riding & Driving Club. All rights reserved. | michael@ridinganddrivingclub.com
            </p>
          </div>
        </footer>
      </main>
    </div>
  )
}
